export const cdtStudioEn = {
  pageTitle: "CDT Studio",
  loading: "Loading base planogram...",
  loadFailed: "Failed to load the base planogram.",
  inProgress: "The base planogram is still being generated. Try again in a moment.",
  itemsSummary: "{{count}} SKUs · {{bays}} bays",
  placementRule: {
    chip: "Placement rule",
    where: "Observed on shelves {{shelves}} · bays {{bays}}",
    title: "Keep {{variant}} on shelves {{shelves}}?",
    detail:
      "The selected grouping may place some of these products on other shelves when the layout " +
      "is regenerated. Saving this rule keeps them on the shelves shown, forming a clean block " +
      "where the fixture allows.",
    // The tree DOES hold this band, so the first sentence would be false. The
    // second one is not: the tree binds the studio, the rule binds the pogger.
    detailTreeHolds:
      "Saving this rule keeps these products on the shelves shown when planograms are generated, " +
      "forming a clean block where the fixture allows.",
    cost:
      "Covers {{cells}} shelf location(s). Width and facing allocation stay flexible, subject to " +
      "other constraints. Full detail on the finalize receipt.",
    promotionNote:
      'To apply this rule separately, "{{variant}}" becomes its own product group within {{parent}}.',
    keep: "Use this rule",
    decline: "Leave placement flexible",
    /* NOT "Undo". This reopens THIS rule's keep/decline question, and it sits
       in the findings band beside the exception rows — so a second control
       called Undo stood next to the work, hard-wired to the restriction
       decision no matter what the user did after it. Owner: "undo button in cdt
       studio doesn't undo the latest, it undoes the variant / restrictions...
       not the exception." The toolbar's Undo is the one that undoes the latest,
       and it is now the only thing wearing that word. */
    changeAnswer: "Change answer",
    keptChip: "Selected — saved when you finish",
    declinedChip: "Placement left flexible",
    /* A KEPT rule leaves the findings list (owner, 2026-09-02: "after the
       placement rules are accepted, they should go somewhere else ... they
       take up too much of the screen and make looking at exceptions too
       hard") and collapses into this compact pill; expanding it keeps the
       Change answer / Leave placement flexible flow reachable. Display relocation only —
       the kept snapshot still writes on finalize. */
    keptSummary: "{{count}} placement rule(s) selected — saved when you finish",
  },
  setup: {
    title: "CDT Studio configuration",
    intro:
      "Set the naming profile and inference rules used whenever CDT Studio opens. Product attributes come from the live category API.",
    summary: "{{attributes}} attributes",
    summaryPlural: "{{attributes}} attributes",
    sections: {
      basic: "Profile and source columns",
      attributes: "Attribute mapping and order",
      semantic: "Semantic roles and inference",
      normalization: "Quantity normalization",
      hierarchy: "Hierarchy moves",
      vertical: "Vertical blocking",
      aesthetics: "Aesthetics",
      advanced: "Advanced profile editor",
    },
    actions: {
      close: "Close",
      reset: "Reset",
      save: "Save configuration",
      addAttribute: "Add attribute",
      addTier: "Add tier",
      remove: "Remove",
      moveUp: "Move up",
      moveDown: "Move down",
    },
    fields: {
      merchant: "Merchant",
      category: "Category",
      profileVersion: "Profile version",
      itemIdentifier: "POG item identifier",
      skuColumn: "SKU column",
      nameColumn: "Name column",
      attributeKey: "Key",
      attributeLabel: "Label",
      sourceColumn: "Source column",
      selectColumn: "Select CSV column",
      pogField: "POG field",
      notMapped: "Not mapped — user fills",
      missingValues: "Missing values",
      blockIdentity: "Block identity order",
      verticalRoles: "Vertical role order",
      brandRole: "Brand role",
      tierRole: "Tier role",
      quantityRole: "Quantity role",
      terminalVariantRole: "Terminal variant role",
      useFirstRegex: "Use first-value regex",
      firstRegex: "First-value regex",
      useLastRegex: "Use last-value regex",
      lastRegex: "Last-value regex",
      normalizationEnabled: "Enable quantity normalization",
      inferFromName: "Infer quantity from product name",
      zeroIsMissing: "Treat zero as missing",
      liquidOuncesAreFluid: "Treat liquid ounces as fluid ounces",
      unitPreference: "Unit preference order",
      liquidNameTerms: "Liquid product-name terms",
      minimumFamilyEvidence: "Minimum family evidence",
      reconciliationMinimum: "Reconciliation ratio minimum",
      reconciliationMaximum: "Reconciliation ratio maximum",
      minFacingsPerShelf: "Minimum facings per shelf",
      roundTripFloorShare: "Round-trip floor share",
      fillFloorShare: "Fill floor share",
      fillDropTolerancePts: "Fill drop tolerance (points)",
      seamPass: "Seam pass",
      blockShare: "Block shelf share",
      fragmentJoinCm: "Fragment join distance (cm)",
      minFillRatio: "Minimum fill ratio",
      preserveRange: "Preserve ranged products",
      onlyIfBetter: "Apply only when score improves",
      brandAttribute: "Brand attribute",
      tierAttribute: "Tier attribute",
      sizeAttribute: "Quantity attribute",
      flavourAttribute: "Terminal variant attribute",
      verticalAttributes: "Vertical attributes",
      tierLabel: "Tier label",
      tierValues: "Tier values",
      lastInBlock: "Values placed last in a block",
      bayOwners: "Whole-bay owners",
      bulkPounds: "Bulk threshold (lb)",
      lowerShelfShare: "Lower-shelf share",
      jitterPacks: "Edge jitter allowance (packs)",
      raggedZeroPacks: "Zero-score raggedness (packs)",
      flagsPerHundredZero: "Zero-score flags per 100 SKUs",
      blockingPenalty: "Blocking severity penalty",
      toCheckPenalty: "To-check severity penalty",
      toReviewPenalty: "To-review severity penalty",
      blockingWeight: "Blocking weight",
      cdtWeight: "CDT weight",
      fullnessWeight: "Fullness weight",
      ergonomicsWeight: "Ergonomics weight",
      verticalSplit: "Vertical blocking split",
      horizontalSplit: "Horizontal blocking split",
      detectorSplit: "Detector CDT split",
      orderingSplit: "Ordering CDT split",
      fullProfileJson: "Full profile JSON",
    },
    tooltips: {
      merchant: "Metadata only. Names the merchant that owns this saved category profile.",
      category: "Metadata only. Names the category configuration; it does not rank the CDT.",
      profileVersion: "Metadata identifying this profile revision; it does not change inference.",
      itemIdentifier:
        "Selects the POG field matched to item-master rows. A wrong choice leaves products missing or mismatched before inference.",
      skuColumn: "CSV column containing the identifier used to join item-master rows to products.",
      nameColumn:
        "CSV product-name column. Names are displayed and may supply quantity evidence when name inference is enabled.",
      attributeKey:
        "Stable engine-facing attribute name used by inference, roles, rules, and overlays.",
      attributeLabel: "Display label for the hierarchy level. It does not change inference.",
      sourceColumn: "CSV column whose values feed this inference attribute.",
      pogField:
        "Optional POG field used to prefill missing item-master rows. It does not change existing rows.",
      missingValues:
        "These case-insensitive spellings become Unspecified and do not create meaningful hierarchy branches.",
      blockIdentity:
        "Resolver only. Major-to-minor attributes used to scope split and orphan audits; this does not reorder initial inference.",
      verticalRoles:
        "Scoring and repair. Ordered attributes that define a vertical merchandising block; this does not select the initial spine.",
      brandRole:
        "Scoring and diagnostics. Attribute used for brand grouping and elimination checks.",
      tierRole:
        "Scoring and diagnostics. Attribute whose configured tier values are checked as horizontal bands.",
      quantityRole:
        "Normalization and resolver. Attribute treated as quantity/size for family parsing and size-order checks.",
      terminalVariantRole:
        "Resolver and scoring. Attribute used for terminal-variant ordering and last-value behavior.",
      useFirstRegex:
        "Enables the saved first-value regex. Disabled means no implicit Original or Regular rule is applied.",
      firstRegex:
        "Inference tie-break. Matching values move first only where shelf evidence states no order.",
      useLastRegex:
        "Enables the saved last-value regex. Disabled means no implicit sensitive or Free & Clear rule is applied.",
      lastRegex:
        "Value ordering. Matching values move to the end; this does not select the attribute's spine level.",
      normalizationEnabled:
        "Enables quantity parsing, name enrichment, and family reconciliation for the quantity role.",
      inferFromName:
        "Uses confirmed quantities in product names to fill missing values or complete bare numbers.",
      zeroIsMissing:
        "Treats numeric zero as missing and eligible for name-based quantity recovery.",
      liquidOuncesAreFluid:
        "Treats ambiguous ounces for liquid products as fluid ounces, keeping volume separate from mass.",
      unitPreference:
        "First matching unit wins when a product name contains several quantities; no hidden unit order is added.",
      liquidNameTerms:
        "Name terms that identify liquids when deciding whether ounces mean fluid ounces.",
      minimumFamilyEvidence:
        "Unambiguous values required before an ambiguous quantity may join a unit family. Higher is more conservative.",
      reconciliationMinimum:
        "Lower bound of the inclusive ratio window for joining ambiguous values to a volume family.",
      reconciliationMaximum:
        "Upper bound of the inclusive ratio window for joining ambiguous values to a volume family.",
      minFacingsPerShelf:
        "Hierarchy moves. Facings per shelf required to treat a moved value as a full-height vertical block.",
      roundTripFloorShare:
        "Hierarchy moves. Minimum shelf-exact reproduction share required after regenerating the changed hierarchy.",
      fillFloorShare:
        "Hierarchy moves. Minimum fill share targeted per run when closing gaps after a move.",
      fillDropTolerancePts:
        "Hierarchy moves. Fill percentage points a move may lose against its before-state.",
      seamPass:
        "Post-move repair: off skips seam squaring, facings-neutral allows it without facing changes, and on allows the full pass.",
      blockShare:
        "Resolver threshold. Shelf coverage required before a value counts as a vertical block; higher requires more coverage.",
      fragmentJoinCm:
        "Direct inference setting. Maximum gap still treated as one fragment; higher values hide more small gaps.",
      minFillRatio:
        "Repair only. The vertical-blocking fix refuses to read seams from a plan below this fill ratio.",
      preserveRange:
        "Repair only. Prevents vertical-blocking repair from removing the last facing of a SKU.",
      onlyIfBetter:
        "Repair only. Keeps a vertical-blocking fix only when the composite aesthetics score improves.",
      brandAttribute:
        "Same setting as Brand role. Groups vertical columns and brand-elimination checks.",
      tierAttribute:
        "Same setting as Tier role. Values are graded through the configured tier order; no order means the measure stands down.",
      sizeAttribute:
        "Same setting as Quantity role. Supplies size-order checks and derived bulk weight.",
      flavourAttribute:
        "Same setting as Terminal variant role. Supplies recurring variant and last-value checks.",
      verticalAttributes:
        "Same setting as Vertical role order. Defines the attribute path grouped as a vertical block.",
      tierLabel:
        "Display name for this tier. Row position, not the label text, defines top-to-bottom order.",
      tierValues:
        "Case-insensitive values belonging to this tier. Unlisted values are not graded as a tier.",
      lastInBlock:
        "Ordering score. Values or names containing these terms should close their block; no list means no implicit rule.",
      bayOwners:
        "Blocking score. Matching blocks must also align their edges with whole-bay boundaries.",
      bulkPounds:
        "Ergonomics score. Minimum derived pack weight considered bulk; 0 disables bulk grading.",
      lowerShelfShare:
        "Ergonomics score. Fraction of shelves, counted from the bottom, treated as the bulk lift zone.",
      jitterPacks:
        "Blocking score. Pack-widths of edge mismatch forgiven before vertical raggedness is penalized.",
      raggedZeroPacks:
        "Blocking score. Excess raggedness in pack-widths at which a vertical block scores zero.",
      flagsPerHundredZero:
        "CDT detector score. Weighted flags per 100 SKUs at which the detector score reaches zero.",
      blockingPenalty: "Detector score penalty assigned to each blocking-severity flag.",
      toCheckPenalty: "Detector score penalty assigned to each to-check flag.",
      toReviewPenalty: "Detector score penalty assigned to each to-review flag.",
      blockingWeight:
        "Overall-score share assigned to blocking. All four component weights must total 100.",
      cdtWeight:
        "Overall-score share assigned to CDT compliance. All four component weights must total 100.",
      fullnessWeight:
        "Overall-score share assigned to shelf gaps and overhang. All four component weights must total 100.",
      ergonomicsWeight:
        "Overall-score share assigned to heavy-product placement. All four component weights must total 100.",
      verticalSplit:
        "Share of the blocking component assigned to vertical blocking; vertical and horizontal must total 1.",
      horizontalSplit:
        "Share of the blocking component assigned to horizontal tiers; vertical and horizontal must total 1.",
      detectorSplit:
        "Share of the CDT component assigned to detector flags; detector and ordering must total 1.",
      orderingSplit:
        "Share of the CDT component assigned to ordering; detector and ordering must total 1.",
      fullProfileJson:
        "Complete validated profile, including JSON-only mappings and rules. Friendly-field edits rewrite this text.",
    },
    advancedReference: {
      title: "JSON-only properties",
      sectionSemantics:
        "Per-section column remapping, conditions, and dropped levels. First matching mapping wins.",
      overlayAttr: "Live attribute represented by a profile level.",
      unitAliases: "Case-insensitive source unit spellings mapped to canonical units.",
      reconciliationBlockAttributes:
        "Attributes defining quantity-evidence groups; false disables reconciliation.",
      firstValueRules: "Per-attribute substrings preferred at the start of sibling value order.",
      lastValueRules:
        "Per-attribute substrings preferred at the end; the terminal rule also feeds last-in-block scoring.",
    },
    help: {
      attributes:
        "Order here is the saved candidate preference used by inference; shelf geometry still decides the hierarchy.",
      commaSeparated: "Separate values with commas; their order is preserved.",
      advanced:
        "Every profile value is available here, including value rules and unit aliases. Valid profile JSON updates the friendly fields; loading always validates and compiles the complete profile.",
    },
    errors: {
      saveFailed: "Could not save the configuration. Please try again.",
      title: "Fix these issues before loading",
      invalidJson: "The full profile editor does not contain valid JSON.",
      persistence: "Could not save the configuration in this browser. Check available storage.",
    },
    saved: "Configuration saved for this category.",
  },
  configuration: {
    settingsLoadFailed: "Could not load merchant settings. Please try again.",
    accessDenied: "You do not have permission to edit CDT configuration.",
    archivedCategory: "Archived categories cannot activate CDT Studio configuration.",
    invalidCategory: "Invalid category.",
    loadFailed: "Could not load this category.",
    loading: "Loading CDT Studio configuration…",
  },
  severity: {
    blocking: "Blocking",
    toCheck: "To check",
    toReview: "To review",
    resolved: "Resolved",
  },
  toolbar: {
    undo: "Undo",
    redo: "Redo",
    detect: "Detect merchandising exceptions",
    fixBlocking: "Fix vertical blocking",
    finalize: "Finalize CDTs",
    downloadCsv: "Save CDTs to file",
  },
  provenance: {
    noNewBreaks: "No new splits — your changes broke nothing.",
    newBreaks: "{{count}} blocks newly broken by your changes",
    regenDelta:
      "Regeneration reproduces {{exact}}/{{total}} products shelf-exact — exceptions on this preview can be regeneration artefacts, not your changes",
    regenShed: "the regenerated plan carries {{count}} shed facing(s)",
    regenOverflow:
      "{{lanes}} shelf(s) hold {{cm}}cm more than their run — packs compress and can overlap",
    moved: "{{count}} SKUs moved vs base",
  },
  evidence: {
    built: "Built: {{order}}",
    want: "Want: {{order}}",
    fix: "Fix",
    acceptNamesVariant: 'Keeping this difference adds a placement rule naming "{{variant}}".',
    acceptNoRow:
      "This scope does not resolve to a single shipped CDT0 value, so accepting records the exception but writes no restriction row.",
    accept: "Keep this intentional difference",
    dismiss: "Dismiss",
  },
  resolver: {
    blockingTooSparse:
      "Vertical blocking not applied: the shelves are only {{fill}}% full, and the fix reads its bands off the plan as it stands. Fill the plan first (or regenerate), then square the blocks.",
    blockingFixed:
      "Vertical blocking: aesthetics {{scoreBefore}} → {{scoreAfter}}. {{added}} facing(s) added, {{removed}} removed across {{skus}} SKU(s); {{unranged}} SKU(s) unranged; {{ragged}}cm of ragged edge left; {{residuals}} block(s) still off their seam.",
    blockingNoGain:
      "Vertical blocking: left alone. Squaring this plan scored {{scoreAfter}} against its current {{scoreBefore}}, so the plan it would have produced is no better than the one you have. Nothing was moved and nothing was unranged.",
    cannotRepairInPlace: "Cannot repair in place",
    cannotRepairInPlaceDetail:
      "The resolver could not move anything within this block's footprint, so the plan is unchanged. Regenerating the whole layout is a separate choice — use the preview toggle.",
    autoResolve: "Auto-resolve",
    partial: "Partial",
    refused: "Not resolved",
    dataOnly: "Data / tree change",
    resetFixes: "Reset fixes",
    repositioned: "{{count}} facings repositioned vs base",
    regenerated:
      "Plan regenerated from the tree — every block reads in tree order; remaining exceptions are measured on the regenerated layout",
    auditStrip:
      "inverted pairs {{inverted}} · lane breaks {{laneBreaks}} · split blocks {{splits}}",
    gradeTitle: "Size gradient · every block",
    gradeSkipped: "{{node}} — {{why}}",
    gradePinned: "{{node}}: {{id}} held high — too tall for the shelf below",
    violation: "Violation",
    needsReview: "Needs review",
    suggested: "Suggested resolution:",
    suggestion: {
      relocate: "Move these products into their group; facing counts may change.",
      resequence: "Reorder products to match the selected sequence.",
      retag: "Update product grouping; nothing moves on the shelf.",
    },
  },
  inference: {
    conventionChip: "Order needs confirmation",
    exceptionsTitle: "Layout assumptions ({{count}})",
    exceptionChip: "Assumption",
    alertChip: "Check data",
    kind: {
      "orient-disagrees": "arrangement differs",
      "no-order": "order needs confirmation",
      "contrary-block": "different arrangement",
      "order-drift": "order differs",
      "gradient-section": "pack heights",
      "size-unparsed": "pack size unreadable",
      "unit-mix": "different measures",
      "pin-backwards": "order reversed",
    },
  },
  panel: {
    closeDirtyTitle: "Close the studio and lose your work?",
    closeDirtyDescription:
      "Dismissals, accepted exceptions, applied fixes and tree edits made here are not saved yet. Closing discards them.",
    closeKeepEditing: "Keep editing",
    closeDiscard: "Discard and close",
    close: "Close",
    location: "Bay {{bay}} · Shelf {{shelf}}",
    previewRegenerated: "Preview regenerated layout",
    inferredFromBasePog: "Inferred CDT from base planogram",
    findings: "Exceptions ({{count}})",
    loadingAttributes: "Loading item master...",
    attributesFailed: "Could not load item-master attributes from the database.",
    shipping: "Saving CDTs...",
    shipped: "Saved {{count}} products to the CDT table — tree {{treeId}}, release {{releaseId}}",
    shipNoMerchflow:
      "No merchflow in context, so there is no decision tree to save to. Open the studio from a merchflow's planogram.",
    storeRulesRequired:
      "This CDT cannot be saved because its per-store measurements do not match the tree shown in CDT Studio. Fix the tree and try again.",
    shippedWithGaps:
      "Saved {{count}} products to the CDT table — tree {{treeId}}, release {{releaseId}}. {{dropped}} product(s) shipped with no placement and will not be generated.",
    orderChanged:
      "Worth a look: the saved CDT cannot hold every product in the order shown here, so generated planograms will sequence some of them differently.",
    staleRules:
      "{{count}} placement rule(s) were not saved: {{rules}} is no longer a section of this tree, so the rule would clear every shelf it names.",
    shippedWithoutRules:
      "The CDT is saved, but the placement rules were not — nothing holds those products to their shelves when planograms are generated.",
    unplacedTitle: "{{count}} product(s) have a CDT but nowhere to stand on this planogram:",
    unplacedCombo: "{{cdt0}} / {{cdt1}}",
    unplacedProduct: "· {{name}} ({{productCode}})",
    unplacedConsequence:
      "Continue and the CDT is saved, but the generator will not place these {{count}} product(s) on any planogram.",
    unplacedContinue: "Finalize anyway",
    gateTitle: "Save placement rules for store planograms?",
    gateOwnsShelf: "{{variant}} stays on shelf {{shelves}}",
    gateOwnsShelves: "{{variant}} stays on shelves {{shelves}}",
    gateNoCost: "Nothing else has to move: no other product is pushed off a shelf.",
    gateEvicts:
      "{{evictions}} existing placement(s) across {{siblings}} other product group(s) would need " +
      "to move.",
    gateCarried: "{{count}} existing group(s) carried through unchanged",
    /* The title asks; this answers it. The modal's own two buttons are the whole
       vocabulary now — no saving state to narrate, no close that means something
       different from cancel. */
    gateOk: "Save placement rules",
    gateCancel: "Cancel",
    gateRefusalNoRows: "There are no restriction rows to save.",
    gateRefusalNoCategory:
      "This merchflow has no category id, so restriction endpoints cannot be used.",
    gateRefusalCannotRead:
      "Existing restrictions could not be loaded. Saving would replace the whole category.",
    gateRefusalEmptyStoreScope:
      "Some stored restriction groups have no store codes. Re-scope or delete them before saving.",
    gateRefusalGeneric: "{{detail}}",
    gateSaveFailed: "The restriction save failed: {{message}}",
  },
  moveReport: {
    budgetTitle: "This move costs facings",
    budgetDescription:
      "It removes {{facings}} facings ({{pct}}% of the plan) and leaves about {{metres}} m of empty shelf. The tree will be right; the shelf will be emptier.",
    budgetFillTitle: "This move costs fill",
    budgetFillDescription:
      "It keeps the facings but costs {{pts}} points of fill ({{before}}% → {{after}}%), leaving about {{metres}} m of shelf uncovered. The tree will be right; the shelf will be emptier.",
    budgetCancel: "Don't move",
    budgetProceed: "Move anyway",
    surgical: "Move executed surgically — everything not asked to change is preserved",
    replay: "Move executed by regeneration",
    mechanismSlide: "Re-ordered the block — the plan was not re-derived from the hierarchy",
    mechanismReband: "Re-stacked the tiers — the plan was not re-derived from the hierarchy",
    mechanismRemeasure:
      "Re-measured in place — the plan survived the hierarchy change; only the contract was re-read",
    mechanismRebuild: "Rebuild — the whole plan was re-derived from the hierarchy",
    mechanismBranchRebuild:
      "Branch re-laid to its new ladder — everything outside that branch stays in place",
    /* MEASURED, never claimed. Every quantity here is read off the
     * committed artifact, so the headline can never contradict the
     * concessions below it (a build shipped "Facings are unchanged"
     * above a report naming 63 re-dealt products). */
    churn: "{{moved}} of {{total}} products re-flowed · {{facings}} had facings re-dealt",
    churnNone: "every product held its exact position",
    noop: "Nothing changed — the move was already in place",
    aesthetics: "Aesthetics {{before}} → {{after}} (each plan scored against its own hierarchy)",
    fill: "Fill {{before}}% → {{after}}%",
    fillWorstRun: "· worst run {{run}}: {{before}}% → {{after}}%",
    fillOverflow: "· overflow {{cm}} cm (counted apart from fill)",
    expectedSales: "Expected sales unchanged — no product added or removed",
    expectedSalesDelta: "Expected sales {{before}} → {{after}}",
  },
  hierarchy: {
    blockedVertical: "vertical",
    blockedHorizontal: "horizontal",
    moveLevelUp: "Move {{label}} up a level",
    moveLevelDown: "Move {{label}} down a level",
    nodeMeta: "{{count}} SKUs · {{flags}} exceptions",
    moveEarlier: "Move {{label}} earlier",
    moveLater: "Move {{label}} later",
    poolingAmbiguous: "blocking ambiguous at {{ratio}} facings per shelf — tap to answer",
    ambiguousLead: "Blocking ambiguous — the studio guessed, you decide (answers persist):",
    moveEarlierShort: "◀ Earlier",
    moveLaterShort: "Later ▶",
    showValues: "List the {{label}} values here",
    hideValues: "Hide the {{label}} values",
    showNextLevel: "Show the level below {{label}}",
    hideNextLevel: "Hide the level below {{label}}",
    liftLevelHere: "Put {{label}} above {{above}}, in this branch only",
    liftLevelShort: "↑",
    liftLevelAbove: "↑ Above {{above}}",
    liftLevelTitle: "Put {{label}} above {{above}}, in this branch only",
    rungScope: "level, within {{scope}}",
    rungDone: "Done",
    wholePlan: "the whole plan",
    siblingPosition: "{{position}} of {{total}} siblings",
    collapseNode: "Collapse",
    expandNode: "Expand",
    selectHint: "Select a node to reorder it among its siblings.",
    expandBranch: "Expand {{label}}",
    collapseBranch: "Collapse {{label}}",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    zoomFit: "Fit",
    zoomFitTitle: "Fit the whole tree in view",
    zoomLevel: "{{percent}}%",
    expandAllNodes: "Expand all nodes",
    collapseAllNodes: "Collapse all nodes",
    expandGraph: "Expand graph",
    collapseGraph: "Collapse graph",
  },
};

export type CdtStudioTranslations = typeof cdtStudioEn;
